import {
  Entity,
  type EntityFields,
  type EntityRecord,
  Fields,
} from '@burnsred/entity';
import { default as DjangoRestFramework } from '@burnsred/entity-duck-namespace-drf';

import { duckAppName } from '@/constants';
import UserEntity, { type UserEntityRecord } from '@/entities/User';

import ApplicationRefEntity, {
  type ApplicationRefEntityRecord,
} from './ApplicationRef';
import type { DateTimeValue } from './dates';
import { FilerFileEntity, type FilerFileEntityRecord } from './Filer';
import type { ScanStatus } from './scan';

class ApplicationBuildEntity extends Entity {
  static paginated = false;

  static paths = {
    apiBase: '/applications/v1/application_build/',
  };

  static fields: EntityFields<ApplicationBuildEntityFields> = {
    uuid: new Fields.IdField(),
    application: new Fields.EntityField({ entity: ApplicationRefEntity }),
    file: new Fields.EntityField({ entity: FilerFileEntity }),
    source: new Fields.EntityField({ entity: FilerFileEntity, blank: true }),
    version: new Fields.IntegerField({ blank: true, local: true }),
    notes: new Fields.TextField({ blank: true }),
    uploaded_by: new Fields.EntityField({
      entity: UserEntity,
      blank: true,
      local: true,
    }),
    is_current: new Fields.BooleanField({ default: true }),
    size_bytes: new Fields.IntegerField({ blank: true, local: true }),
    file_count: new Fields.IntegerField({ blank: true, local: true }),
    has_index_html: new Fields.BooleanField({ blank: true, local: true }),
    status: new Fields.CharField({ blank: true, local: true }),
    validation_error: new Fields.CharField({ blank: true, local: true }),
    scan_status: new Fields.CharField({ blank: true, local: true }),
    source_scan_status: new Fields.CharField({ blank: true, local: true }),
    has_source: new Fields.BooleanField({ blank: true, local: true }),
    activate_on_publish: new Fields.BooleanField({ blank: true, local: true }),
    published_at: new Fields.DateTimeField({ blank: true, local: true }),
    created: new Fields.DateTimeField({ blank: true, local: true }),
    modified: new Fields.DateTimeField({ blank: true, local: true }),
  };

  static dataToRecord(data: Partial<ApplicationBuildData>) {
    return super.dataToRecord(data) as ApplicationBuildEntityRecord;
  }
}

export type ApplicationBuildEntityFields = {
  uuid: string;
  application: ApplicationRefEntityRecord | null;
  file: FilerFileEntityRecord | null;
  source: FilerFileEntityRecord | null;
  version: number;
  notes: string;
  uploaded_by: UserEntityRecord | null;
  is_current: boolean;
  size_bytes: number;
  file_count: number;
  has_index_html: boolean;
  status: string;
  validation_error: string;
  scan_status: string;
  source_scan_status: string;
  has_source: boolean;
  activate_on_publish: boolean;
  published_at: DateTimeValue;
  created: DateTimeValue;
  modified: DateTimeValue;
};

export type ApplicationBuildEntityRecord =
  EntityRecord<ApplicationBuildEntityFields>;

export type ApplicationBuildData = {
  uuid: string;
  application?: { uuid: string; name: string; slug: string };
  file: { id: number | string; name: string; url: string };
  source: { id: number | string; name: string; url: string } | null;
  version: number;
  notes: string;
  uploaded_by: {
    id: number | string;
    uuid?: string;
    email: string;
    first_name: string;
    last_name: string;
    full_name: string;
  } | null;
  is_current: boolean;
  size_bytes: number;
  file_count: number;
  has_index_html: boolean;
  status: string;
  validation_error: string;
  scan_status?: ScanStatus;
  source_scan_status?: ScanStatus;
  has_source: boolean;
  activate_on_publish: boolean;
  published_at: string | null;
  created: string;
  modified: string;
};

ApplicationBuildEntity.duck = new DjangoRestFramework({
  app: duckAppName,
  entity: ApplicationBuildEntity,
  name: 'ApplicationBuildEntity',
});

export default ApplicationBuildEntity;
